import { useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { API, api } from "./api";
import type { Message, Participant, Session } from "./types";
import Avatar from "./avatar";
const reactions = ["👍", "❤️", "😂", "😮", "😢", "🔥"];
export default function Chat({
  initial,
  onExpired,
}: {
  initial: Session;
  onExpired: () => void;
}) {
  const [room, setRoom] = useState(initial.room);
  const [messages, setMessages] = useState<Message[]>([]);
  const [notices, setNotices] = useState<{ id: string; text: string; createdAt: string }[]>([]);
  const [people, setPeople] = useState<Participant[]>([]);
  const [online, setOnline] = useState(new Set<string>());
  const [typing, setTyping] = useState(new Set<string>());
  const [text, setText] = useState("");
  const [reply, setReply] = useState<Message | null>(null);
  const [menu, setMenu] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(room.status === "WAITING");
  const [expired, setExpired] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [recording, setRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const socket = useRef<Socket | null>(null),
    bottom = useRef<HTMLDivElement>(null),
    fileInput = useRef<HTMLInputElement>(null),
    recorder = useRef<MediaRecorder | null>(null);
  useEffect(() => {
    api.history(room.inviteCode, initial.sessionToken).then((x) => {
      setMessages(x.messages);
      setPeople(x.participants);
      x.messages
        .filter((m: Message) => m.participant.id !== initial.participant.id && !m.readAt)
        .forEach((m: Message) => socket.current?.emit("message:read", { id: m.id }));
    });
    const s = io(API, {
      auth: { code: room.inviteCode, token: initial.sessionToken },
    });
    socket.current = s;
    s.on("message:new", (m: Message) => {
      setMessages((v) => [...v, m]);
      if (m.participant.id !== initial.participant.id)
        s.emit("message:read", { id: m.id });
      notify(m);
    });
    s.on("message:read", ({ id, readAt }) =>
      setMessages((v) =>
        v.map((m) => (m.id === id ? { ...m, readAt } : m)),
      ),
    );
    s.on("message:edited", (x: any) =>
      setMessages((v) => v.map((m) => (m.id === x.id ? { ...m, ...x } : m))),
    );
    s.on("message:deleted", ({ id }) =>
      setMessages((v) =>
        v.map((m) =>
          m.id === id
            ? { ...m, body: null, deletedAt: new Date().toISOString() }
            : m,
        ),
      ),
    );
    s.on("reaction:changed", (x: any) =>
      setMessages((v) =>
        v.map((m) =>
          m.id !== x.messageId
            ? m
            : {
                ...m,
                reactions: x.active
                  ? [
                      ...m.reactions,
                      { emoji: x.emoji, participantId: x.participantId },
                    ]
                  : m.reactions.filter(
                      (r) =>
                        !(
                          r.emoji === x.emoji &&
                          r.participantId === x.participantId
                        ),
                    ),
              },
        ),
      ),
    );
    s.on("room:started", (r: any) => setRoom((v) => ({ ...v, ...r })));
    s.on("room:updated", (r: any) => setRoom((v) => ({ ...v, ...r })));
    s.on("participant:updated", (x: any) =>
      setPeople((v) => v.map((p) => (p.id === x.id ? { ...p, ...x } : p))),
    );
    s.on("room:invite-rotated", ({ inviteCode }) => {
      const old = room.inviteCode;
      localStorage.setItem(
        `tempchat:${inviteCode}`,
        JSON.stringify({
          sessionToken: initial.sessionToken,
          adminToken: initial.adminToken,
        }),
      );
      const pin = localStorage.getItem(`tempchat:pin:${old}`);
      if (pin) localStorage.setItem(`tempchat:pin:${inviteCode}`, pin);
      s.auth = { code: inviteCode, token: initial.sessionToken };
      history.replaceState({}, "", `/c/${inviteCode}`);
      setRoom((v) => ({ ...v, inviteCode }));
    });
    s.on("room:expired", () => setExpired(true));
    s.on("participant:kicked", () => {
      alert("You were removed from this chat.");
      location.href = "/";
    });
    s.on("presence:join", (p: Participant) => {
      setPeople((v) => (v.some((x) => x.id === p.id) ? v : [...v, p]));
      setOnline((v) => new Set(v).add(p.id));
      setNotices((v) => [...v, { id: crypto.randomUUID(), text: `${p.nickname} joined the chat`, createdAt: new Date().toISOString() }]);
    });
    s.on("presence:leave", ({ participantId, nickname }) => {
      setOnline((v) => {
        const n = new Set(v);
        n.delete(participantId);
        return n;
      });
      setNotices((v) => [...v, { id: crypto.randomUUID(), text: `${nickname || "Someone"} left the chat`, createdAt: new Date().toISOString() }]);
    });
    s.on("typing", ({ participantId, active }) =>
      setTyping((v) => {
        const n = new Set(v);
        active ? n.add(participantId) : n.delete(participantId);
        return n;
      }),
    );
    return () => {
      s.disconnect();
    };
  }, []);
  useEffect(() => {
    let active = true;
    const sync = async () => {
      try {
        const x = await api.history(room.inviteCode, initial.sessionToken);
        if (!active) return;
        setMessages((current) => {
          const transient = new Map(current.map((m) => [m.id, m]));
          return x.messages.map((m: Message) => ({
            ...transient.get(m.id),
            ...m,
          }));
        });
        setPeople(x.participants);
      } catch {}
    };
    const timer = setInterval(sync, 2500);
    const onFocus = () => sync();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [room.inviteCode, initial.sessionToken]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, notices.length, typing.size]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const syncViewport = () => {
      document.documentElement.style.setProperty(
        "--chat-height",
        `${viewport?.height || window.innerHeight}px`,
      );
      document.documentElement.style.setProperty(
        "--chat-top",
        `${viewport?.offsetTop || 0}px`,
      );
      window.scrollTo(0, 0);
    };
    document.body.classList.add("chat-open");
    syncViewport();
    viewport?.addEventListener("resize", syncViewport);
    viewport?.addEventListener("scroll", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    return () => {
      document.body.classList.remove("chat-open");
      document.documentElement.style.removeProperty("--chat-height");
      document.documentElement.style.removeProperty("--chat-top");
      viewport?.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("scroll", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
    };
  }, []);
  const left = room.expiresAt
    ? Math.max(0, new Date(room.expiresAt).getTime() - now)
    : 600_000;
  const clock = `${String(Math.floor(left / 60000)).padStart(2, "0")}:${String(Math.floor((left % 60000) / 1000)).padStart(2, "0")}`;
  useEffect(() => {
    if (room.expiresAt && left === 0) setExpired(true);
  }, [left]);
  const send = () => {
    if (!text.trim()) return;
    socket.current?.emit(
      "message:send",
      { body: text, replyToId: reply?.id },
      (r: any) => {
        if (!r.ok) return;
        setText("");
        setReply(null);
        socket.current?.emit("typing", false);
      },
    );
  };
  const grouped = useMemo(() => messages, [messages]);
  const timeline = useMemo(() => [
    ...grouped.map((message) => ({ type: "message" as const, id: message.id, createdAt: message.createdAt, message })),
    ...notices.map((notice) => ({ type: "notice" as const, ...notice })),
  ].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()), [grouped, notices]);
  const mention = people
    .filter((p) => text.endsWith("@") || text.match(/@\w*$/))
    .slice(0, 5);
  async function upload(file: File, voice = false) {
    try {
      if (file.size > 20 * 1024 * 1024)
        return alert("Files must be 20 MB or smaller.");
      const { key } = await api.upload(
        room.inviteCode,
        initial.sessionToken,
        file,
      );
      const kind = voice
        ? "VOICE"
        : file.type.startsWith("image/")
          ? "IMAGE"
          : file.type.startsWith("video/")
            ? "VIDEO"
            : "FILE";
      socket.current?.emit("message:send", {
        kind,
        attachmentKey: key,
        attachmentName: file.name,
        attachmentMime: file.type,
        attachmentSize: file.size,
        replyToId: reply?.id,
      });
      setReply(null);
    } catch (e: any) {
      alert(
        e.code === "ROOM_STORAGE_LIMIT"
          ? "This room has used its 100 MB storage allowance."
          : "Upload failed.",
      );
    }
  }
  async function toggleRecording() {
    if (recording) {
      recorder.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: Blob[] = [];
      const r = new MediaRecorder(stream);
      recorder.current = r;
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setRecordTime(0);
        const blob = new Blob(chunks, { type: r.mimeType });
        upload(
          new File([blob], `voice-${Date.now()}.webm`, { type: r.mimeType }),
          true,
        );
      };
      r.start();
      setRecording(true);
      setRecordTime(0);
    } catch {
      alert("Microphone access is required to record a voice message.");
    }
  }
  useEffect(() => {
    if (!recording) return;
    const t = setInterval(
      () =>
        setRecordTime((v) => {
          if (v >= 59) {
            recorder.current?.stop();
            return 60;
          }
          return v + 1;
        }),
      1000,
    );
    return () => clearInterval(t);
  }, [recording]);
  function notify(m: Message) {
    if (m.participant.id === initial.participant.id) return;
    const mentioned = m.body
      ?.toLowerCase()
      .includes(`@${initial.participant.nickname.toLowerCase()}`);
    if (document.hidden && Notification.permission === "granted")
      new Notification(
        mentioned ? "You were mentioned" : m.participant.nickname,
        { body: m.body || m.attachmentName || "New message" },
      );
    if (navigator.vibrate) navigator.vibrate(mentioned ? [100, 80, 100] : 80);
    try {
      new Audio(
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAACAqKiA",
      )
        .play()
        .catch(() => {});
    } catch {}
  }
  if (expired)
    return (
      <div className="expired">
        <div>⌛</div>
        <h1>This chat has expired</h1>
        <p>Messages and files have been permanently deleted.</p>
        <a className="primary" href="/">
          Create a new chat
        </a>
      </div>
    );
  return (
    <div className="chat-shell">
      <header className={left <= 60_000 ? "warning" : ""}>
        <button
          className="icon-btn mobile"
          onClick={() => setParticipantsOpen(true)}
        >
          ☰
        </button>
        <div className="chat-title" onClick={() => setParticipantsOpen(true)}>
          <strong>{room.title}</strong>
          <span>
            {room.status === "WAITING"
              ? "Waiting for the first guest"
              : `${online.size || people.length} here`}
          </span>
        </div>
        <button className="timer" onClick={() => setShareOpen(true)}>
          <span>{room.status === "WAITING" ? "READY" : clock}</span>
          <small>
            {room.status === "WAITING"
              ? "10 min after first guest"
              : "until deletion"}
          </small>
        </button>
        <button className="icon-btn" onClick={() => setMenu(!menu)}>
          •••
        </button>
        {menu && (
          <AdminMenu
            room={room}
            me={initial.participant}
            people={people}
            emit={(x: any) => socket.current?.emit("admin:action", x)}
            share={() => setShareOpen(true)}
            close={() => setMenu(false)}
          />
        )}
      </header>
      {left <= 60_000 && room.status === "ACTIVE" && (
        <div className="countdown-alert">
          <span>This chat disappears in {Math.ceil(left / 1000)} seconds</span>
          <a href="https://wwchats.com/?utm_source=tempchat&utm_medium=referral&utm_campaign=countdown">
            Need more time? WWChats →
          </a>
        </div>
      )}
      <div className="chat-body">
        <aside className={participantsOpen ? "open" : ""}>
          <div className="aside-head">
            <b>People · {people.length}/10</b>
            <button onClick={() => setParticipantsOpen(false)}>×</button>
          </div>
          {people.map((p) => (
            <div className="person" key={p.id}>
              <Avatar seed={p.avatarSeed} />
              <span>
                <b>
                  {p.nickname}
                  {p.id === initial.participant.id ? " (you)" : ""}
                </b>
                <small>
                  {p.isAdmin ? "Creator" : online.has(p.id) ? "online" : "away"}
                </small>
              </span>
              {initial.participant.isAdmin && !p.isAdmin && (
                <button
                  title="Remove"
                  onClick={() =>
                    confirm(
                      `Remove ${p.nickname}? The invite link will change.`,
                    ) &&
                    socket.current?.emit("admin:action", {
                      type: "kick",
                      participantId: p.id,
                    })
                  }
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button className="invite" onClick={() => setShareOpen(true)}>
            ＋ Invite people
          </button>
        </aside>
        <section className="conversation">
          {room.status === "WAITING" && (
            <div className="system-pill">
              Share the link. The 10-minute timer starts when your first guest
              joins.
            </div>
          )}
          <div className="messages">
            {timeline.map((item, i) => item.type === "notice" ? (
              <div className="service-message" key={item.id}>{item.text}</div>
            ) : (() => {
              const m = item.message;
              const previous = timeline[i - 1];
              return (
              <Bubble
                key={m.id}
                message={m}
                previous={previous?.type === "message" ? previous.message : undefined}
                mine={m.participant.id === initial.participant.id}
                me={initial.participant}
                onReply={() => setReply(m)}
                react={(emoji: string) =>
                  socket.current?.emit("reaction:toggle", {
                    messageId: m.id,
                    emoji,
                  })
                }
                remove={() =>
                  socket.current?.emit("message:delete", { id: m.id })
                }
                edit={() => {
                  const body = prompt("Edit message", m.body || "");
                  if (body)
                    socket.current?.emit("message:edit", { id: m.id, body });
                }}
                loadFile={async () =>
                  (
                    await api.fileUrl(
                      room.inviteCode,
                      initial.sessionToken,
                      m.id,
                    )
                  ).url
                }
              />
              );
            })())}
            {typing.size > 0 && (
              <div className="typing">
                {people.find((p) => typing.has(p.id))?.nickname} is typing{" "}
                <i>•••</i>
              </div>
            )}
            <div ref={bottom} />
          </div>
          {mention.length > 0 && text.includes("@") && (
            <div className="mentions">
              {mention.map((p) => (
                <button
                  key={p.id}
                  onClick={() =>
                    setText((v) => v.replace(/@\w*$/, `@${p.nickname} `))
                  }
                >
                  <Avatar seed={p.avatarSeed} size={28} />
                  {p.nickname}
                </button>
              ))}
            </div>
          )}{" "}
          {reply && (
            <div className="reply-bar">
              <span>
                <b>Reply to {reply.participant.nickname}</b>
                {reply.body || reply.attachmentName}
              </span>
              <button onClick={() => setReply(null)}>×</button>
            </div>
          )}
          <div className="composer">
            {recording ? (
              <>
                <div className="recording">
                  <i /> Recording 0:{String(recordTime).padStart(2, "0")} / 1:00
                </div>
                <button className="send danger" onClick={toggleRecording}>
                  ■
                </button>
              </>
            ) : (
              <>
                <button
                  className="attach"
                  onClick={() => fileInput.current?.click()}
                >
                  ＋
                </button>
                <input
                  ref={fileInput}
                  hidden
                  type="file"
                  onChange={(e) =>
                    e.target.files?.[0] && upload(e.target.files[0])
                  }
                />
                <textarea
                  rows={1}
                  placeholder="Message"
                  value={text}
                  onFocus={() => setTimeout(() => bottom.current?.scrollIntoView(), 250)}
                  onChange={(e) => {
                    setText(e.target.value);
                    socket.current?.emit("typing", !!e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                {text.trim() ? (
                  <button className="send" onClick={send}>
                    ➤
                  </button>
                ) : (
                  <button className="mic" onClick={toggleRecording}>
                    🎙
                  </button>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      {shareOpen && <Share room={room} close={() => setShareOpen(false)} />}
    </div>
  );
}
function Bubble({
  message: m,
  previous,
  mine,
  me,
  onReply,
  react,
  remove,
  edit,
  loadFile,
}: any) {
  const same = previous?.participant.id === m.participant.id && !m.deletedAt;
  const [counts, setCounts] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaFailed, setMediaFailed] = useState(false);
  useEffect(() => {
    if (!["IMAGE", "VIDEO", "VOICE"].includes(m.kind) || m.deletedAt) return;
    let active = true;
    let objectUrl = "";
    loadFile()
      .then((url: string) => {
        objectUrl = url;
        if (active) setMediaUrl(url);
      })
      .catch(() => active && setMediaFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [m.id, m.kind, m.deletedAt]);
  const openFile = async () => window.open(await loadFile(), "_blank");
  return (
    <div className={`message-row ${mine ? "mine" : ""} ${same ? "same" : ""}`}>
      {!mine && !same && <Avatar seed={m.participant.avatarSeed} size={34} />}
      <div className="bubble-wrap">
        {!mine && !same && <b className="sender">{m.participant.nickname}</b>}
        <div
          className={`bubble ${m.deletedAt ? "deleted" : ""}`}
          onDoubleClick={onReply}
        >
          {m.deletedAt ? (
            <em>Message deleted</em>
          ) : (
            <>
              {m.replyToId && <div className="quoted">Reply to a message</div>}
              {m.kind === "IMAGE" &&
                (mediaUrl ? (
                  <button className="media-preview" onClick={openFile}>
                    <img
                      src={mediaUrl}
                      alt={m.attachmentName || "Shared image"}
                    />
                  </button>
                ) : (
                  <div className="media-placeholder">
                    {mediaFailed ? "Preview unavailable" : "Loading image…"}
                  </div>
                ))}
              {m.kind === "VIDEO" &&
                (mediaUrl ? (
                  <video
                    className="inline-video"
                    src={mediaUrl}
                    controls
                    playsInline
                  />
                ) : (
                  <div className="media-placeholder">
                    {mediaFailed ? "Video unavailable" : "Loading video…"}
                  </div>
                ))}
              {m.kind === "VOICE" &&
                (mediaUrl ? (
                  <div className="voice-player">
                    <span>🎙</span>
                    <audio src={mediaUrl} controls preload="metadata" />
                  </div>
                ) : (
                  <div className="voice">
                    <span>🎙</span>
                    <small>
                      {mediaFailed
                        ? "Voice message unavailable"
                        : "Loading voice message…"}
                    </small>
                  </div>
                ))}
              {m.kind === "FILE" && (
                <button className="file" onClick={openFile}>
                  📄{" "}
                  <span>
                    {m.attachmentName}
                    <small>
                      {m.attachmentSize
                        ? `${(m.attachmentSize / 1024 / 1024).toFixed(1)} MB`
                        : ""}
                    </small>
                  </span>
                </button>
              )}
              {m.body && <span className="body">{renderMentions(m.body)}</span>}
              <span className="meta">
                {m.editedAt ? "edited · " : ""}
                {new Date(m.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {mine ? (m.readAt ? " ✓✓" : " ✓") : ""}
              </span>
            </>
          )}
        </div>
        {!m.deletedAt && (
          <div className="bubble-actions">
            <button onClick={onReply}>↩</button>
            <button onClick={() => setCounts(!counts)}>☺</button>
            {mine && m.body && <button onClick={edit}>✎</button>}
          {mine && <button onClick={remove}>⌫</button>}
          </div>
        )}
        {counts && (
          <div className="reaction-picker">
            {reactions.map((x) => (
              <button
                key={x}
                onClick={() => {
                  react(x);
                  setCounts(false);
                }}
              >
                {x}
              </button>
            ))}
          </div>
        )}
        {m.reactions?.length > 0 && (
          <div className="reaction-list">
            {Object.entries(
              m.reactions.reduce(
                (a: any, r: any) => ((a[r.emoji] = (a[r.emoji] || 0) + 1), a),
                {},
              ),
            ).map(([e, n]) => (
              <button key={e} onClick={() => react(e)}>
                {e} {String(n)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function renderMentions(body: string) {
  return body
    .split(/(@[A-Za-z]+(?:[A-Za-z]+)?)/g)
    .map((x, i) => (x.startsWith("@") ? <mark key={i}>{x}</mark> : x));
}
function Share({ room, close }: { room: any; close: () => void }) {
  const url = `${location.origin}/c/${room.inviteCode}`,
    pin = localStorage.getItem(`tempchat:pin:${room.inviteCode}`);
  const copy = () => navigator.clipboard.writeText(url);
  return (
    <div className="modal-bg" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-x" onClick={close}>
          ×
        </button>
        <div className="room-badge">⌁</div>
        <h2>Invite someone</h2>
        <p>The timer starts when the first guest joins.</p>
        <div className="share-link">
          <span>{url}</span>
          <button onClick={copy}>Copy</button>
        </div>
        {room.hasPin && (
          <p className="hint">
            PIN: <b>{pin || "••••"}</b> · Share it separately from the link.
          </p>
        )}
        <button
          className="primary"
          onClick={() =>
            navigator
              .share?.({ title: "Join my TempChat", url })
              .catch(() => {})
          }
        >
          Share link
        </button>
      </div>
    </div>
  );
}
function AdminMenu({ room, me, people, emit, share, close }: any) {
  return (
    <div className="dropdown">
      <button
        onClick={() => {
          share();
          close();
        }}
      >
        ⌁ Share invite link
      </button>
      <button
        onClick={() => {
          const nickname = prompt("Your temporary name", me.nickname);
          if (nickname) emit({ type: "nickname", nickname });
          close();
        }}
      >
        ☺ Change my name
      </button>
      <button
        onClick={() => {
          Notification.requestPermission();
          close();
        }}
      >
        ♢ Enable notifications
      </button>
      {me.isAdmin && (
        <>
          <button
            onClick={() => {
              const title = prompt("Room name", room.title);
              if (title) emit({ type: "title", title });
              close();
            }}
          >
            ✎ Rename chat
          </button>
          <button
            onClick={() => {
              emit({ type: "lock", locked: !room.locked });
              close();
            }}
          >
            {room.locked ? "○ Open" : "⊘ Close"} new joins
          </button>
          <button
            className="danger-text"
            onClick={() =>
              confirm("Destroy this chat now? This cannot be undone.") &&
              emit({ type: "destroy" })
            }
          >
            ⌫ Destroy chat
          </button>
        </>
      )}
      <small>{people.length}/10 participants</small>
    </div>
  );
}
